// BG Drilling addition, 29.09.2026. This whole file is a BG Drilling change.
//
// What the left strip „Структура" of the parts screen remembers in this
// browser (localStorage), so it does not open on the same place every time:
//   - the last opened category (or „all parts"). The menu item „Артикули"
//     (/part/) opens there. Grigor, 29.09.2026: „хубаво да запазва последната
//     отворена структура, защото иначе всеки път, много е неудобно, отварям
//     всеки път едно и също";
//   - which branches of the tree are open;
//   - the favourite categories (the star), shown at the top of the strip.
//     Grigor: „трябва да има опция за запазване на категория, която се излиза
//     в фейворитс или звездичка".
// Every read and write is inside try/catch: a private window or blocked site
// data gives an empty memory, never a broken screen.
// Re-apply after an InvenTree update.
import { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';

const KEY_LAST = 'bgd-structure-last';
const KEY_EXPANDED = 'bgd-structure-expanded';
const KEY_FAVOURITES = 'bgd-structure-favourites';

/** The most favourites kept. A sixth star pushes out the oldest one. */
export const BGD_FAVOURITES_MAX = 5;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return fallback;
    }
    const value = JSON.parse(raw);
    return (value ?? fallback) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // no storage - nothing is remembered, the screen still works
  }
}

/** The last opened category: a pk as text, or 'index' for all parts. */
export function bgdReadLastCategory(): string {
  const value = readJson<string>(KEY_LAST, '');
  return /^\d+$/.test(`${value}`) ? `${value}` : 'index';
}

export function bgdRememberCategory(id: string | number | undefined) {
  const value = id === undefined || id === null || id === '' ? 'index' : id;
  writeJson(KEY_LAST, `${value}`);
}

/**
 * The index route /part/: straight to the last opened category. Used in
 * router.tsx in place of `<Navigate to='category/index/' />`.
 */
export function BgdPartIndexRedirect() {
  return <Navigate to={`category/${bgdReadLastCategory()}/`} replace />;
}

export function bgdReadExpanded(): Record<number, boolean> {
  const value = readJson<Record<number, boolean>>(KEY_EXPANDED, {});
  return typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function bgdWriteExpanded(value: Record<number, boolean>) {
  // only the open ones, so the stored map stays small
  const open: Record<number, boolean> = {};
  Object.keys(value).forEach((key: string) => {
    if (value[Number(key)]) {
      open[Number(key)] = true;
    }
  });
  writeJson(KEY_EXPANDED, open);
}

/** The favourite categories, in the order they were starred. */
export function useBgdFavourites(): [
  number[],
  (pk: number) => void,
  (pk: number) => boolean
] {
  const [favourites, setFavourites] = useState<number[]>(() => {
    const value = readJson<number[]>(KEY_FAVOURITES, []);
    return Array.isArray(value)
      ? value.filter((pk: any) => Number.isInteger(pk))
      : [];
  });

  const toggle = useCallback((pk: number) => {
    setFavourites((current: number[]) => {
      let next: number[];
      if (current.includes(pk)) {
        next = current.filter((item: number) => item !== pk);
      } else {
        next = [...current, pk];
        if (next.length > BGD_FAVOURITES_MAX) {
          next = next.slice(next.length - BGD_FAVOURITES_MAX);
        }
      }
      writeJson(KEY_FAVOURITES, next);
      return next;
    });
  }, []);

  const isFavourite = useCallback(
    (pk: number) => favourites.includes(pk),
    [favourites]
  );

  return [favourites, toggle, isFavourite];
}
